import { getAblyClient } from "./ablyManager";

// ─── Analysis room (Ably presence) ───────────────────────────────────────────
// Both analyst screens for one (matchId, analystId) pair enter presence on the
// same channel, so each side can see whether its counterpart is actually open.
//
//   VideoTab     → ROLE_VIDEO
//   InputBoard   → ROLE_KEYBOARD  (also InputBoardCoordinates)
//
// Presence members are re-entered automatically by ably-js after a reconnect,
// and Ably removes a member a few seconds after its connection drops.

export const ROLE_VIDEO = "video";
export const ROLE_KEYBOARD = "keyboard";

export const PEER_OF = {
  [ROLE_VIDEO]: ROLE_KEYBOARD,
  [ROLE_KEYBOARD]: ROLE_VIDEO,
};

const roomChannelName = (matchId, analystId) =>
  `analysis_room_${matchId}_${analystId}`;

/**
 * Enter the room and keep the caller informed about who else is in it.
 *
 * @param {string|number} matchId
 * @param {string|number} analystId
 * @param {string} role       - ROLE_VIDEO | ROLE_KEYBOARD
 * @param {function} onMembers - receives [{ clientId, role }]
 * @returns {Promise<{leave: function, refresh: function}>}
 */
export const joinAnalysisRoom = async (matchId, analystId, role, onMembers) => {
  const client = getAblyClient();
  const channel = client.channels.get(roomChannelName(matchId, analystId));

  const refresh = async () => {
    try {
      const members = await channel.presence.get();
      onMembers(
        (members || []).map((m) => ({
          clientId: m.clientId,
          role: m.data?.role,
        }))
      );
    } catch (err) {
      console.error("Failed to read analysis room presence", err);
    }
  };

  const handler = () => refresh();

  await channel.presence.subscribe(handler);
  await channel.presence.enter({
    role,
    matchId: String(matchId),
    analystId: String(analystId),
  });
  await refresh();

  const leave = async () => {
    try {
      channel.presence.unsubscribe(handler);
      await channel.presence.leave();
    } catch (err) {
      console.error("Failed to leave analysis room", err);
    }
    try {
      await channel.detach();
    } catch (err) {
      // detach can fail on an already-closed connection — nothing to clean up.
    }
  };

  // `refresh` re-reads the member list on demand — used by the Retry button.
  return { leave, refresh };
};
